import { type ReactNode, useMemo } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { canAccessAdminRoute, getStoredUserRole } from '@/lib/staff-roles'
import { REPUTATION_INBOX_ENABLED } from '@/lib/reputationInbox'
import { useEdition } from '@/hooks/useEdition'
import { cn } from '@/lib/utils'

interface MarketingShellProps {
  title: string
  description?: string
  /** Optional right-aligned page action (e.g. a date-range picker) rendered
   *  next to the heading on the same row, mirroring the Reports shell so the
   *  two pages share a consistent layout. */
  actions?: ReactNode
  children: ReactNode
}

interface MarketingTab {
  id: string
  label: string
  href: string
}

const TABS: MarketingTab[] = [
  { id: 'overview', label: 'Overview', href: '/admin/marketing/overview' },
  { id: 'promo-codes', label: 'Promo codes', href: '/admin/marketing/promo-codes' },
  { id: 'loyalty', label: 'Loyalty program', href: '/admin/marketing/loyalty' },
  { id: 'enrolment', label: 'Enrolment', href: '/admin/marketing/enrolment' },
  { id: 'members', label: 'Members', href: '/admin/marketing/members' },
  { id: 'qr-codes', label: 'QR codes', href: '/admin/marketing/qr-codes' },
  { id: 'segments', label: 'Segments', href: '/admin/marketing/segments' },
  { id: 'campaigns', label: 'Campaigns', href: '/admin/marketing/campaigns' },
  { id: 'automations', label: 'Automations', href: '/admin/marketing/automations' },
  { id: 'reviews', label: 'Reviews', href: '/admin/marketing/reviews' },
  // Hidden while Google Business Profile API access is pending
  // (lib/reputationInbox.ts).
  ...(REPUTATION_INBOX_ENABLED
    ? [{ id: 'reputation', label: 'Reputation', href: '/admin/marketing/reputation' }]
    : []),
  { id: 'recovery', label: 'Guest recovery', href: '/admin/marketing/recovery' },
  { id: 'gift-cards', label: 'Gift cards', href: '/admin/marketing/gift-cards' },
  { id: 'card-promos', label: 'Bank cards', href: '/admin/marketing/card-promos' },
  { id: 'posters', label: 'Posters', href: '/admin/marketing/posters' },
]

export function MarketingShell({ title, description, actions, children }: MarketingShellProps) {
  const location = useLocation()
  const { posEnabled } = useEdition()

  // Marketing is admin-only except for Bank cards, which managers own too
  // (staff-roles.ts). Filtering here rather than rendering all eight tabs
  // keeps a manager from clicking straight into a route guard bounce.
  // Bank card promos are a POS-checkout feature, so the tab goes too on
  // marketing-edition deploys (the path is POS-only in staff-roles.ts).
  const tabs = useMemo(() => {
    const role = getStoredUserRole()
    const all = posEnabled ? TABS : TABS.filter((t) => t.id !== 'card-promos')
    if (role === 'admin' || role === '') return all
    return all.filter((t) => canAccessAdminRoute(role, t.href))
  }, [posEnabled])

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {description && (
            <p className="mt-1 text-muted-foreground max-w-3xl">{description}</p>
          )}
        </div>
        {actions && <div className="lg:pt-1">{actions}</div>}
      </div>

      {/* A one-tab row is noise, not navigation (the manager case). On
          marketing-edition deploys the sidebar lists every tool directly,
          so the row would be a duplicate nav — hidden entirely there. */}
      {posEnabled && tabs.length > 1 && (
      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="navigation" aria-label="Marketing sections">
        {tabs.map((tab) => {
          const isActive =
            location.pathname === tab.href ||
            location.pathname.startsWith(tab.href + '/')
          return (
            <Link
              key={tab.id}
              to={tab.href}
              className={cn(
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </div>
      )}

      {children}
    </div>
  )
}
